import { CircularProgress } from "@mui/material";
import { ButtonProps } from "@mui/material/Button";
import Grid from "@mui/material/Unstable_Grid2";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { format, parseISO } from "date-fns";
import { isEmpty, map } from "lodash";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { Card } from "../../../components/Card";
import { CountUp } from "../../../components/CountUp";
import { Dialog } from "../../../components/Dialog";
import { PreviewHtml } from "../../../components/PreviewHtml";
import { listDetailsCampaign } from "../../../services/campaigns";
import { Campaign } from "../../../types/campaign";
import { useStyles } from "../styles";
import { LinearProgress } from "../../../components/LinearProgress";

type CampaignProps = {
  data: Campaign | null;
  open: boolean;
  onClose: () => void;
};

export function CampaignDetails({ data, open, onClose }: CampaignProps) {
  const styles = useStyles();

  const details = useQuery(
    ["details", data?.ID_CAMPANHA],
    () => listDetailsCampaign(data?.ID_CAMPANHA),
    { enabled: Boolean(data?.ID_CAMPANHA) }
  );

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Fechar",
        onClick: onClose,
      },
    ];
  }, [onClose]);

  const hasEmail: boolean = useMemo(() => {
    return details.data?.details?.mensagem.includes(".html");
  }, [details.data?.details]);

  const progress: number = useMemo(() => {
    const processed = details.data?.status
      ?.filter((status) => status.status_envio_externo !== "EM ESPERA")
      .map((status) => status.quantidade)
      .reduce((previous, current) => previous + current, 0);

    const allSends = details.data?.status
      ?.map((status) => status.quantidade)
      .reduce((previous, current) => previous + current, 0);

    return (processed / allSends) * 100;
  }, [details.data?.status]);

  return (
    <Dialog
      onClose={onClose}
      fullWidth
      maxWidth="md"
      title={data?.nome_campanha}
      open={open}
      actions={actions}
    >
      {details.isLoading ? (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress />
        </Stack>
      ) : isEmpty(details.data?.details) ? (
        <Typography
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight={600}
        >
          Não foi possível buscar os detalhes dessa campanha
        </Typography>
      ) : (
        <Stack spacing={2} className={styles.rootContent}>
          <Stack direction="row" spacing={2}>
            <Card variant="outlined">
              <Typography
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={600}
              >
                Criação
              </Typography>
              <Typography
                variant="subtitle2"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {format(
                  parseISO(details.data.details?.data_insert?.replace("Z", "")),
                  "dd/MM/yyyy HH:mm"
                )}
              </Typography>
            </Card>
            <Card variant="outlined">
              <Typography
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={600}
              >
                Agendado para
              </Typography>
              <Typography
                variant="subtitle2"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {format(
                  parseISO(details.data.details?.data_envio?.replace("Z", "")),
                  "dd/MM/yyyy HH:mm"
                )}
              </Typography>
            </Card>
            <Card variant="outlined">
              <Typography
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={600}
              >
                Finalizado em
              </Typography>
              <Typography
                variant="subtitle2"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {details.data.details?.data_processado
                  ? format(
                      parseISO(
                        details.data.details?.data_processado?.replace("Z", "")
                      ),
                      "dd/MM/yyyy HH:mm"
                    )
                  : "Em processamento"}
              </Typography>
            </Card>
            <Card variant="outlined">
              <Typography
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight={600}
              >
                Progresso
              </Typography>
              <LinearProgress value={progress} />
            </Card>
          </Stack>
          <Stack direction="row" spacing={2}>
            {details.data.details?.mensagem && (
              <Card variant="outlined">
                {hasEmail ? (
                  <PreviewHtml link={details.data.details?.mensagem} />
                ) : (
                  <>
                    <Typography
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight={600}
                    >
                      Mensagem
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      {details.data.details?.mensagem}
                    </Typography>
                  </>
                )}
              </Card>
            )}
            {details.data.details?.whatsapp_url && (
              <Card variant="outlined">
                <Typography
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight={600}
                >
                  Mídia
                </Typography>
                <div className={styles.rootImgMedia}>
                  <img
                    className={styles.imgMidia}
                    src={details.data.details?.whatsapp_url}
                    alt="midia-campaign"
                  />
                </div>
              </Card>
            )}
          </Stack>
          <Grid container spacing={2}>
            {map(details.data?.status, (detail) => (
              <Grid xs={4}>
                <Card className={styles.cardStatus} variant="outlined">
                  <Typography
                    variant="body2"
                    display="flex"
                    textAlign="center"
                    fontWeight={600}
                  >
                    {detail.status_envio_externo}
                  </Typography>
                  <CountUp align="center" end={detail?.quantidade} />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}
    </Dialog>
  );
}
